import { applyCkoSession } from '@cart-services/core/paymentService'
import { CountryLocaleMap } from '@constants'
import { datadogRum } from '@datadog/browser-rum'
import { useApiGetFpPaymentRequest } from '@hooks-api/fortune/useApiGetFpPayment'
import { type ECommercePaymentExecution, type ECommerceOrderType } from '@hooks-api/types/dataModel/eCommercePayments'
import { type UserPaymentTokenInfo } from '@hooks-api/types/dataModel/eCommercePayments'
import { PaymentMethodType, PaymentServiceType, type PaymentMethod } from '@hooks-api/types/dataModel/payment'
import { CARD_SOURCE, isCkoFlowModule } from '@payments'
import { useEffect, useRef } from 'react'
import { useCheckout } from '../hooks/use-checkout'
import { getCurrentSelectedMethod, getModule } from '../utils/utility-process-credit-card-pay-module'
import { type AppliedPaymentModule } from '../utils/utility-process-pay-module'
interface UseCreditCardModuleProps {
  orderId?: string | null
  orderType?: ECommerceOrderType
  selectedPaymentMethod?: PaymentMethod
  rememberPaymentCardToken?: boolean
  isProcessingCheckout?: boolean
  setIsPaymentProcessing?: (isPaymentProcessing: boolean) => void
  creditCardMethods: PaymentMethod[]
  selectedMethodId: number
  cardSource: string
  userPaymentTokenInfo?: UserPaymentTokenInfo
  setUserPaymentTokenInfos?: (userPaymentTokenInfo: UserPaymentTokenInfo[]) => void
  setPaymentExecution?: (paymentExecution: ECommercePaymentExecution) => void
  isChecked: boolean
  setConfiguredPaymentModule: (paymentModule: AppliedPaymentModule | undefined) => void
  /**
   * Flow SDK 選卡當下的 inline mount target resolver。只有仍走 inline drop-in 的線（STM）傳入；
   * merchant 信用卡已改 dialog 掛載，不傳 → 只設定 appKey，SDK 掛載延後到 dialog 開啟。
   */
  getCkoMountContainerId?: (selectedMethodId: number) => string
  /** Flow SDK init 必填（B2C 對齊）。Init 階段沒 transactionId，先帶 placeholder URL；Exec API 才會帶含 transactionId 的真實 URL。 */
  paymentSuccessUrl?: string
  paymentFallbackUrl?: string
  /** Flow SDK init：remember card / Alipay HK 時需要 billing email 寫回 cardholder。 */
  billingEmail?: string
}

export const useCreditCardPayment = (props: UseCreditCardModuleProps) => {
  const {
    orderId,
    orderType,
    selectedPaymentMethod,
    rememberPaymentCardToken,
    cardSource,
    userPaymentTokenInfo,
    setUserPaymentTokenInfos,
    setPaymentExecution,
    isProcessingCheckout = false,
    setIsPaymentProcessing,
    creditCardMethods,
    selectedMethodId,
    setConfiguredPaymentModule,
    isChecked,
    getCkoMountContainerId,
    paymentSuccessUrl,
    paymentFallbackUrl,
    billingEmail,
  } = props
  const paymentModule = getModule(selectedMethodId, creditCardMethods)
  const { env, countryCode } = useCheckout()

  const appliedMethodType = selectedPaymentMethod?.type
  const isFlowEnabled = selectedPaymentMethod?.service_type === PaymentServiceType.CHECKOUTPAY

  const { destructuredData: appliedPaymentInfo, isValidating: isValidatingPayment } = useApiGetFpPaymentRequest(isChecked
    ? {
      order_type: orderType,
      order_id: orderId,
      payment_method_id: selectedPaymentMethod?.id,
      remember_payment_card_token: rememberPaymentCardToken,
      // 只在 applied method 走 Flow SDK 時帶 init payload 欄位（對齊 B2C `useWpPaymentService`）
      payment_success_url: isFlowEnabled ? paymentSuccessUrl : undefined,
      payment_fallback_url: isFlowEnabled ? paymentFallbackUrl : undefined,
      email: isFlowEnabled && (rememberPaymentCardToken || appliedMethodType === PaymentMethodType.ALIPAY_HK)
        ? billingEmail
        : undefined,
    }
    : undefined, {
    /** 因為後端是抓最後一次 fetch payment/payment，所以重複資料也要重新 fetch */
    dedupingInterval: 0,
    revalidateOnFocus: false,
    disabled: isProcessingCheckout,
  })

  useEffect(() => {
    setIsPaymentProcessing?.(isValidatingPayment)
  }, [isValidatingPayment, setIsPaymentProcessing])

  const prevConfigRef = useRef<{ appId: string, appKey: string } | null>(null)
  useEffect(function configurePaymentModule() {
    if (!appliedPaymentInfo) return

    if (!paymentModule) {
      throw new Error('Wrong paymentModule')
    }

    const { payment_service_provider_info, user_payment_token_infos, payment_transaction_id } = appliedPaymentInfo
    const { app_id, app_key, ecpay_token } = payment_service_provider_info
    const currentSelectedMethod = getCurrentSelectedMethod(selectedMethodId, creditCardMethods)
    const appKey = (currentSelectedMethod?.service_type === PaymentServiceType.ECPAY ? ecpay_token : app_key) || ''
    const newConfig = {
      appId: app_id || '',
      appKey,
    }

    const prevConfig = prevConfigRef.current
    const configChanged = !prevConfig
      || prevConfig.appId !== newConfig.appId
      || prevConfig.appKey !== newConfig.appKey

    if (isCkoFlowModule(paymentModule)) {
      if (configChanged) {
        paymentModule.configurePayment({ appId: newConfig.appId, appEnv: env, appKey: newConfig.appKey, locale: CountryLocaleMap[countryCode] })
        prevConfigRef.current = newConfig
      }
      setUserPaymentTokenInfos?.(user_payment_token_infos)
      setPaymentExecution?.({
        payment_transaction_id,
        service_pay_info: null,
        user_payment_token_info: cardSource === CARD_SOURCE.REMEMBERED ? (userPaymentTokenInfo || user_payment_token_infos[0]) : undefined,
        payment_success_url: '',
        payment_fallback_url: '',
      })
      // session 未變且已 mount 時跳過，防 revalidate 觸發 iframe 重掛清掉輸入中的卡號
      if (getCkoMountContainerId) {
        const latestSessionId = appliedPaymentInfo.payment_service_provider_info?.checkoutpay_session_info?.id ?? null
        const needsRemount = (latestSessionId !== null && latestSessionId !== paymentModule.getPaymentSessionId())
          || !paymentModule.isMounted()
        if (needsRemount) {
          applyCkoSession(paymentModule, appliedPaymentInfo, env, getCkoMountContainerId(selectedMethodId), { countryCode })
            .catch((error) => {
              console.error('[useCreditCardPayment] applyCkoSession failed:', error)
              datadogRum.addError(error, { function: '[useCreditCardPayment] applyCkoSession failed' })
            })
        }
      }
      setConfiguredPaymentModule(paymentModule)
      return
    }

    // Non-Flow（ECPay / TapPay）：credentials 不變就不重新 init SDK
    if (!configChanged) return

    setUserPaymentTokenInfos?.(user_payment_token_infos)
    setPaymentExecution?.({
      payment_transaction_id,
      service_pay_info: null,
      user_payment_token_info: cardSource === CARD_SOURCE.REMEMBERED ? (userPaymentTokenInfo || user_payment_token_infos[0]) : undefined,
      payment_success_url: '',
      payment_fallback_url: '',
    })
    paymentModule.configurePayment({
      appId: newConfig.appId,
      appEnv: env,
      appKey: newConfig.appKey,
    })
    prevConfigRef.current = newConfig
    setConfiguredPaymentModule(paymentModule)
  }, [appliedPaymentInfo, cardSource, creditCardMethods, env, getCkoMountContainerId, paymentModule, selectedMethodId, setConfiguredPaymentModule, setPaymentExecution, setUserPaymentTokenInfos, userPaymentTokenInfo])

  return {
    appliedPaymentInfo,
    isValidatingPayment,
  }
}
